import {
  Box,
  BoxProps,
  Heading,
  ListItem,
  OrderedList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
} from "@chakra-ui/react";
import React from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface PrettyMarkdownProps extends BoxProps {
  children: string;
  components?: Partial<Components>;
}

/**
 * A markdown renderer that uses native Chakra UI components for proper theming and styling.
 * Renders headings, lists, tables, paragraphs, and inline elements with consistent Chakra styling.
 */
export const PrettyMarkdown: React.FC<PrettyMarkdownProps> = ({
  children,
  components,
  ...boxProps
}) => {
  let markdownComponents: Components = {
    thead: ({ children }) => <Thead>{children}</Thead>,
    tbody: ({ children }) => <Tbody>{children}</Tbody>,
    tr: ({ children }) => <Tr>{children}</Tr>,
    h1: ({ children }) => (
      <Heading as="h1" size="md" mt={4} mb={2}>
        {children}
      </Heading>
    ),
    h2: ({ children }) => (
      <Heading as="h2" size="sm" mt={3} mb={2}>
        {children}
      </Heading>
    ),
    h3: ({ children }) => (
      <Heading as="h3" size="xs" mt={2} mb={1}>
        {children}
      </Heading>
    ),
    p: ({ children }) => (
      <Text mb={2} lineHeight="tall">
        {children}
      </Text>
    ),
    ul: ({ children }) => (
      <UnorderedList mb={2} pl={4}>
        {children}
      </UnorderedList>
    ),
    ol: ({ children }) => (
      <OrderedList mb={2} pl={4}>
        {children}
      </OrderedList>
    ),
    li: ({ children }) => <ListItem mb={1}>{children}</ListItem>,
    table: ({ children }) => (
      <Box overflowX="auto" mb={4}>
        <Table size="sm" variant="simple">
          {children}
        </Table>
      </Box>
    ),
    th: ({ children }) => (
      <Th fontSize="xs" fontWeight="bold">
        {children}
      </Th>
    ),
    td: ({ children }) => <Td fontSize="xs">{children}</Td>,
    strong: ({ children }) => (
      <strong style={{ fontWeight: 600 }}>{children}</strong>
    ),
    em: ({ children }) => <em>{children}</em>,
    a: ({ href, children }) => {
      if (
        Array.isArray(children) &&
        children.length === 1 &&
        typeof children[0] === "string"
      ) {
        const isCitation = /^\^\d+$/.test(children[0]);
        if (isCitation) {
          const num = children[0].slice(1);
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                verticalAlign: "super",
                marginLeft: "0.3em",
                fontSize: "0.75em",
                textDecoration: "none",
                color: "var(--chakra-colors-blue-500)",
              }}
            >
              {num}
            </a>
          );
        }
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    code: ({ children }) => (
      <Box
        as="code"
        px={1}
        py={0.5}
        bg="gray.100"
        borderRadius="sm"
        fontSize="xs"
        fontFamily="mono"
      >
        {children}
      </Box>
    ),
  };

  if (components) {
    markdownComponents = {
      ...markdownComponents,
      ...components,
    };
  }

  return (
    <Box {...boxProps}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};
